import styled from '@emotion/styled';

import { media } from '@myrealtrip/modash';
import { ButtonGeneral, Text } from '@myrealtrip/web-ui';

import Show from '~components/Show';

import type { PopularPlaceDetail } from '~models/Transportation/PointToPointTicket';

interface SectionNotice {
  text: 'HOT' | 'NEW';
  backgroundColor: string;
  color: string;
}

interface SuggestedPlaceSection {
  title: string;
  notice?: SectionNotice;
  places: Array<PopularPlaceDetail | [PopularPlaceDetail, PopularPlaceDetail]>;
}

interface Props {
  className?: string;
  sections: SuggestedPlaceSection[];
  onClickSinglePlace: (place: PopularPlaceDetail) => void;
  onClickRoundPlace: (place: [PopularPlaceDetail, PopularPlaceDetail]) => void;
}

const SuggestedPlace = ({
  className = '',
  sections = [],
  onClickSinglePlace,
  onClickRoundPlace,
}: Props) => {
  return (
    <SuggestedPlaceSection className={className}>
      {sections.map((section) => (
        <Show key={section.title} when={section.places.length > 0}>
          <SuggestionContainer>
            <TitleWrapper>
              <Title typography="paragraphBold16">{section.title}</Title>
              {section.notice && (
                <NoticeBadge backgroundColor={section.notice.backgroundColor}>
                  <NoticeBadgeText typography="captionBold9" color={section.notice.color}>
                    {section.notice.text}
                  </NoticeBadgeText>
                </NoticeBadge>
              )}
            </TitleWrapper>
            <SuggestionPlaceList>
              {section.places.map((place) => {
                if (Array.isArray(place)) {
                  return (
                    <PlaceBadge
                      key={`${place[0].code}-${place[1].code}`}
                      priority="tertiary"
                      size="small"
                      onClick={() => onClickRoundPlace([place[0], place[1]])}
                    >
                      <BadgeText typography="headlineNormal14">
                        {place[0].koreanLabel} - {place[1].koreanLabel}
                      </BadgeText>
                    </PlaceBadge>
                  );
                }

                return (
                  <PlaceBadge
                    key={place.code}
                    priority="tertiary"
                    size="small"
                    onClick={() => onClickSinglePlace(place)}
                  >
                    <BadgeText typography="headlineNormal14">{place.koreanLabel}</BadgeText>
                  </PlaceBadge>
                );
              })}
            </SuggestionPlaceList>
          </SuggestionContainer>
        </Show>
      ))}
    </SuggestedPlaceSection>
  );
};

const SuggestedPlaceSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding-bottom: 80px;

  ${media.minMobile} {
    gap: 0px;
    padding-left: 20px;
  }
`;

const SuggestionContainer = styled.div`
  display: flex;
  flex-direction: column;

  &:not(:last-of-type) {
    margin: 0 0 24px;
  }
`;

const SuggestionPlaceList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0 20px;

  ${media.minMobile} {
    padding: 0px;
  }
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 20px;

  ${media.minMobile} {
    padding: 0 20px 10px 0px;
  }
`;

const Title = styled(Text)``;

const NoticeBadge = styled.span<{ backgroundColor: string }>`
  height: 16px;
  display: flex;
  padding: 0px 4.5px 0.5px 4.5px;
  justify-content: center;
  align-items: center;
  border-radius: 10px;
  backdrop-filter: blur(25px);
  background-color: ${({ backgroundColor }) => backgroundColor};
  margin-left: 4px;
`;

const NoticeBadgeText = styled(Text)`
  color: ${({ theme: { palettes } }) => palettes.white};
`;

const PlaceBadge = styled(ButtonGeneral)``;

const BadgeText = styled(Text)`
  color: ${({ theme: { palettes } }) => palettes.gray[700]};
`;

export default SuggestedPlace;
